import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import urlMap from '../../data/urlMap.json';

/**
 * Maintains a single <link rel="canonical"> in <head>, updated on every route
 * change.
 *
 * Why this is needed: the app is client-rendered from one index.html, so
 * without this every page would either have no canonical or share one. The
 * canonical is built from the pathname alone - query strings and hashes are
 * deliberately dropped, so /products?utm_source=x and /products#section both
 * declare /products as the one indexable URL.
 *
 * Renders nothing.
 */
function SeoCanonical() {
    const { pathname } = useLocation();

    useEffect(() => {
        const siteUrl = urlMap.siteUrl.replace(/\/$/, '');
        const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
        const href = `${siteUrl}${cleanPath}`;

        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', href);

        // Keep the noindex pages out of the index even though they are
        // reachable and linked from the site.
        const noindexPaths = urlMap.pages.filter((p) => p.noindex).map((p) => p.path);
        const existing = document.querySelector('meta[name="robots"][data-seo-canonical]');
        if (noindexPaths.includes(cleanPath)) {
            if (!existing) {
                const meta = document.createElement('meta');
                meta.setAttribute('name', 'robots');
                meta.setAttribute('content', 'noindex, follow');
                meta.setAttribute('data-seo-canonical', '');
                document.head.appendChild(meta);
            }
        } else if (existing) {
            existing.remove();
        }
    }, [pathname]);

    return null;
}

export default SeoCanonical;
